/**
 * Modal mit allen Schichten UND Kalender-Einträgen (Notiz/Abwesenheit) eines Tages.
 *
 * Schichten: Abrechnung (Admin/Manager), Beleg, Bearbeiten/Löschen.
 * Einträge:  Notiz/Frei/Urlaub/Krank/Wunschfrei anlegen, bearbeiten, löschen.
 */

import { useState } from 'react';
import { useDeleteShift, useSettleShift, useBonusDecision } from '../data/shifts';
import { useDeleteCalendarEntry } from '../data/calendarEntries';
import type { LocalShift, LocalLocation, LocalCalendarEntry } from '../db/schema';
import { formatDate, formatMoney } from '../lib/format';
import { isOpenEndPending, isSettleableNow, shiftHours } from '../lib/payroll';
import { ENTRY_TYPE_META } from '../lib/calendarEntries';
import { useAuth, useCanManage } from '../store/auth';
import { ReceiptModal } from './ReceiptModal';
import { SwapOfferModal } from './SwapOfferModal';
import { HandoverTimeModal } from './HandoverTimeModal';
import { hapticError, hapticSuccess } from '../lib/haptics';

interface Props {
  isoDate: string;
  shifts: LocalShift[];
  entries: LocalCalendarEntry[];
  locationsById: Map<string, LocalLocation>;
  onClose: () => void;
  onCreate: () => void;
  onEdit: (shift: LocalShift) => void;
  onCreateEntry: () => void;
  onEditEntry: (entry: LocalCalendarEntry) => void;
}

const STATUS_META: Record<string, { text: string; cls: string }> = {
  pending: { text: 'ausstehend', cls: 'bg-amber-500/20 text-amber-300' },
  approved: { text: 'freigegeben', cls: 'bg-emerald-500/20 text-emerald-300' },
  rejected: { text: 'abgelehnt', cls: 'bg-red-500/20 text-red-300' },
};

export function DayDetailModal({
  isoDate,
  shifts,
  entries,
  locationsById,
  onClose,
  onCreate,
  onEdit,
  onCreateEntry,
  onEditEntry,
}: Props) {
  const remove = useDeleteShift();
  const settle = useSettleShift();
  const removeEntry = useDeleteCalendarEntry();
  const bonusDecision = useBonusDecision();
  const me = useAuth((s) => s.user);
  const token = useAuth((s) => s.token);
  const breakDeduction = me?.breakDeductionEnabled ?? false;
  const canSettle = useCanManage();
  // Phase F (Kapitel 11.6): NUR der Chef entscheidet über Zusatzstunden —
  // useCanManage() liefert Owner/globalen Admin, keine Filialleitung.
  const canDecideBonus = useCanManage();

  const [busy, setBusy] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [swapOfferFor, setSwapOfferFor] = useState<LocalShift | null>(null);
  const [handoverFor, setHandoverFor] = useState<LocalShift | null>(null);

  async function decideBonus(shiftId: string, decision: 'approved' | 'rejected') {
    if (!token || bonusDecision.isPending) return;
    try {
      await bonusDecision.mutateAsync({ id: shiftId, decision, token });
      hapticSuccess();
    } catch (err) {
      hapticError();
      alert(err instanceof Error ? err.message : 'Zusatzstunden-Entscheidung fehlgeschlagen');
    }
  }

  const settleable = shifts.filter((s) => s.status !== 'cancelled');
  const openCount = settleable.filter((s) => !s.settledAt).length;
  const allSettled = settleable.length > 0 && openCount === 0;

  async function settleDay(settled: boolean) {
    if (busy) return;
    setBusy(true);
    try {
      // Tagesgeld-Entnahmen (Nachtrag 11.09.2026) nicht per Sammel-Aufheben
      // wieder öffnen — das Geld ist schon raus; Storno nur einzeln bewusst.
      // Gesamtprüfung 11.09.2026: offene Zusatzstunden-Anträge erst entscheiden.
      const targets = settleable.filter((s) =>
        settled
          ? !s.settledAt && s.bonusStatus !== 'pending' && isSettleableNow(s)
          : !!s.settledAt && !s.dailyPayoutAt,
      );
      for (const s of targets) {
        await settle.mutateAsync({ id: s.id, settled });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-2 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md max-h-[92vh] sm:max-h-[90vh] overflow-y-auto border border-border/60">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{formatDate(isoDate)}</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 !min-h-0 px-2"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        {/* Schichten */}
        {shifts.length === 0 ? (
          <p className="text-slate-400 text-sm mb-4">Keine Schichten an diesem Tag.</p>
        ) : (
          <ul className="space-y-2 mb-4">
            {shifts.map((s) => {
              const loc = locationsById.get(s.locationId);
              const h = shiftHours(s, { breakDeduction });
              const wage = s.hourlyWage == null ? null : h * s.hourlyWage;
              return (
                <li
                  key={s.id}
                  className="bg-slate-900/50 rounded p-3 border-l-4"
                  style={{ borderLeftColor: loc?.color ?? '#64748b' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {loc?.name ?? '?'}{' '}
                        {s.pending && (
                          <span className="ml-1 badge bg-amber-500/20 text-amber-300">ungesynct</span>
                        )}
                        {s.settledAt && (
                          <span className="ml-1 badge bg-emerald-500/20 text-emerald-300">
                            {s.dailyPayoutAt ? 'Tagesgehalt entnommen' : 'abgerechnet'}
                          </span>
                        )}
                      </p>
                      {isOpenEndPending(s) ? (
                        <p className="text-sm text-amber-300 font-medium">
                          ab {s.startTime} · Endzeit noch eintragen
                        </p>
                      ) : (
                        <p className="text-sm text-slate-400">
                          {s.startTime}–{s.endTime} · {h.toFixed(2).replace('.', ',')} h ·{' '}
                          <span className="text-emerald-400">{formatMoney(wage)}</span>
                          {s.bonusHours > 0 && (
                            <span className="text-amber-300 text-xs ml-1">
                              (inkl. +{s.bonusHours.toFixed(2).replace('.', ',')} h)
                            </span>
                          )}
                        </p>
                      )}
                      <p className="text-xs text-slate-500 capitalize">
                        {s.status === 'planned' ? 'geplant' : s.status === 'done' ? 'erledigt' : 'abgesagt'}
                        {s.origin === 'self_reported' && (
                          <span className="ml-1 normal-case text-slate-500">· eingesprungen</span>
                        )}
                      </p>
                      {/* Phase F (Kapitel 11.3/11.5/11.6): Zusatzstunden-Bestätigungsstatus.
                          bonusStatus kommt bereits serverseitig als 'none' zurück, wenn der
                          Aufrufer kein managerCanSeeBonusHours an diesem Standort hat — hier
                          also einfach anzeigen, keine zusätzliche Sichtbarkeits-Prüfung nötig. */}
                      {s.bonusStatus === 'pending' && (
                        <p className="text-xs text-amber-300 mt-1">
                          + {(s.requestedBonusHours ?? 0).toFixed(2).replace('.', ',')} Std. Zusatzstunden beantragt — wartet auf den Chef
                        </p>
                      )}
                      {s.bonusStatus === 'approved' && (
                        <p className="text-xs text-emerald-400 mt-1">Zusatzstunden bestätigt</p>
                      )}
                      {s.bonusStatus === 'rejected' && (
                        <p className="text-xs text-slate-500 mt-1">
                          Zusatzstunden abgelehnt (beantragt {(s.requestedBonusHours ?? 0).toFixed(2).replace('.', ',')} Std.)
                        </p>
                      )}
                      {s.vacationOverrideBy && (
                        <p className="text-xs text-red-300 mt-1">Trotz genehmigter Abwesenheit eingetragen</p>
                      )}
                      {s.note && <p className="text-xs text-slate-500 italic mt-1">„{s.note}"</p>}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {canDecideBonus && s.bonusStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => decideBonus(s.id, 'approved')}
                            disabled={bonusDecision.isPending}
                            className="text-xs bg-emerald-600/80 hover:bg-emerald-600 rounded px-2 py-1 disabled:opacity-50"
                          >
                            Bestätigen
                          </button>
                          <button
                            onClick={() => decideBonus(s.id, 'rejected')}
                            disabled={bonusDecision.isPending}
                            className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2 py-1 disabled:opacity-50"
                          >
                            Zusatzst. ablehnen
                          </button>
                        </>
                      )}
                      {canSettle &&
                        s.status !== 'cancelled' &&
                        // Gesamtprüfung 11.09.2026 (spiegelt den Server): eigene
                        // Tagesgeld-Entnahme nicht selbst stornieren, offene
                        // Zusatzstunden-Anträge nicht mit abrechnen.
                        !(s.dailyPayoutAt && s.userId === me?.id) &&
                        (s.settledAt || (s.bonusStatus !== 'pending' && isSettleableNow(s))) && (
                        <button
                          onClick={() => settle.mutate({ id: s.id, settled: !s.settledAt })}
                          disabled={busy || settle.isPending}
                          className={`text-xs rounded px-2 py-1 disabled:opacity-50 ${
                            s.settledAt ? 'bg-surface-3 hover:bg-slate-600' : 'bg-emerald-600/80 hover:bg-emerald-600'
                          }`}
                        >
                          {s.settledAt ? 'Aufheben' : 'Abrechnen'}
                        </button>
                      )}
                      {s.userId === me?.id && s.status === 'planned' && (
                        <button
                          onClick={() => setSwapOfferFor(s)}
                          className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-2 py-1"
                        >
                          Tauschen
                        </button>
                      )}
                      {/* Phase M (Kapitel 18, Entscheidung #21): Übergabezeit —
                          nur für eigene, geplante Schichten, unabhängig vom
                          Schichttausch (dort wechselt die ganze Schicht den
                          Besitzer, hier nur der Übergabe-Zeitpunkt). */}
                      {s.userId === me?.id && s.status === 'planned' && !s.settledAt && !s.dailyPayoutAt && (
                        <button
                          onClick={() => setHandoverFor(s)}
                          className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-2 py-1"
                        >
                          Übergabe
                        </button>
                      )}
                      {/* Gesamtprüfung 11.09.2026: abgerechnete eigene Schichten
                          ändert nur der Chef; Tagesgeld-Schichten niemand selbst. */}
                      {!(s.userId === me?.id && (s.dailyPayoutAt || (s.settledAt && !canSettle))) && (
                        <>
                          <button
                            onClick={() => onEdit(s)}
                            className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-2 py-1"
                          >
                            Bearbeiten
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Schicht löschen?')) remove.mutate(s.id);
                            }}
                            disabled={remove.isPending}
                            className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2 py-1 disabled:opacity-50"
                          >
                            Löschen
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {shifts.length > 0 && (
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setShowReceipt(true)}
              className="flex-1 bg-surface-3 hover:bg-slate-600 transition rounded py-2 text-sm font-medium"
            >
              Beleg
            </button>
            {canSettle && settleable.length > 0 && (
              openCount > 0 ? (
                <button
                  onClick={() => settleDay(true)}
                  disabled={busy}
                  className="flex-1 bg-emerald-600/80 hover:bg-emerald-600 transition rounded py-2 text-sm font-medium disabled:opacity-50"
                >
                  {openCount === settleable.length ? 'Tag abrechnen' : `${openCount} offene abrechnen`}
                </button>
              ) : (
                allSettled && (
                  <button
                    onClick={() => settleDay(false)}
                    disabled={busy}
                    className="flex-1 bg-surface-3 hover:bg-slate-600 transition rounded py-2 text-sm font-medium disabled:opacity-50"
                  >
                    Abrechnung aufheben
                  </button>
                )
              )
            )}
          </div>
        )}

        {/* Notizen & Abwesenheiten */}
        <div className="border-t border-border/60 pt-3 mb-3">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">
            Notizen &amp; Abwesenheiten
          </p>
          {entries.length === 0 ? (
            <p className="text-sm text-slate-500 mb-2">Keine Einträge.</p>
          ) : (
            <ul className="space-y-2 mb-2">
              {entries.map((e) => {
                const meta = ENTRY_TYPE_META[e.type];
                const showStatus = e.type !== 'note';
                const st = STATUS_META[e.status];
                return (
                  <li
                    key={e.id}
                    className="bg-slate-900/50 rounded p-3 border-l-4"
                    style={{ borderLeftColor: meta.color }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          <span style={{ color: meta.color }}>{meta.label}</span>
                          {e.halfDay && <span className="text-xs text-slate-400"> · halber Tag</span>}
                          {e.pending && (
                            <span className="ml-1 badge bg-amber-500/20 text-amber-300">ungesynct</span>
                          )}
                          {showStatus && st && <span className={`ml-1 badge ${st.cls}`}>{st.text}</span>}
                        </p>
                        {e.title && <p className="text-sm text-slate-300 truncate">{e.title}</p>}
                        {e.note && <p className="text-xs text-slate-500 italic mt-0.5">„{e.note}"</p>}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <button
                          onClick={() => onEditEntry(e)}
                          className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-2 py-1"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Eintrag löschen?')) removeEntry.mutate(e.id);
                          }}
                          disabled={removeEntry.isPending}
                          className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2 py-1 disabled:opacity-50"
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            onClick={onCreateEntry}
            className="w-full bg-surface-3 hover:bg-slate-600 transition rounded py-2 text-sm font-medium"
          >
            + Notiz / Frei / Urlaub
          </button>
        </div>

        <button
          onClick={onCreate}
          className="w-full btn"
        >
          + Neue Schicht für {formatDate(isoDate)}
        </button>
      </div>

      {showReceipt && (
        <ReceiptModal
          isoDate={isoDate}
          shifts={shifts}
          locationsById={locationsById}
          employeeName={me?.name}
          breakDeduction={breakDeduction}
          onClose={() => setShowReceipt(false)}
        />
      )}

      {swapOfferFor && (
        <SwapOfferModal
          shift={swapOfferFor}
          onClose={() => setSwapOfferFor(null)}
          onSaved={() => setSwapOfferFor(null)}
        />
      )}

      {handoverFor && (
        <HandoverTimeModal
          shift={handoverFor}
          onClose={() => setHandoverFor(null)}
          onSaved={() => setHandoverFor(null)}
        />
      )}
    </div>
  );
}
